import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import api from '../api/api';

const QrOdoo = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [locations, setLocations] = useState([]);           
  const [selectedLocation, setSelectedLocation] = useState(''); 
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [printQuantities, setPrintQuantities] = useState({});

  // Cargar productos y ubicaciones en paralelo
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [productsRes, locationsRes] = await Promise.all([
          api.get('/qr-odoo/products'),
          api.get('/qr-odoo/locations')        
        ]);

        const fetchedProducts = productsRes.data.products || [];
        setProducts(fetchedProducts);
        setFilteredProducts(fetchedProducts);
        
        const fetchedLocations = locationsRes.data || [];
        setLocations(fetchedLocations);
        
        // Seleccionar por defecto la primera ubicación si existe
        if (fetchedLocations.length > 0) {
          setSelectedLocation(fetchedLocations[0].complete_name || fetchedLocations[0].name || '');
        }
      } catch (err) {
        const msg = err.response?.data?.detail || 'Error al conectar con Odoo';
        setError(msg);
        setSnackbarMessage(msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtro de búsqueda dinámico
useEffect(() => {
  if (!searchTerm.trim()) {
    setFilteredProducts(products);
    return;
  }

  const term = searchTerm.toLowerCase().trim();

  const filtered = products.filter(p => {
    const nameMatch = p.name?.toString().toLowerCase().includes(term);
    const barcodeMatch = p.barcode?.toString().toLowerCase().includes(term);
    const codeMatch = p.default_code?.toString().toLowerCase().includes(term);

    return nameMatch || barcodeMatch || codeMatch;
  });

  setFilteredProducts(filtered);
}, [searchTerm, products]);

  // Descargar código QR como imagen
  const handleDownloadQR = async (product) => {
    try {
      const response = await api.get(`/qr-odoo/generate/${product.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${product.default_code || product.id}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbarMessage(`QR descargado: ${product.name}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Error al descargar QR');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Impresión directa P1 mediante Web Serial API
  const handlePrintP1 = async (product) => {
    if (printingId) return;
    setPrintingId(product.id);

    let port;
    try {
      // REGRESAMOS A 280: Para que use el ancho completo de los 48mm del papel para el texto
      const QR_SIZE_PX = 280; 
      const DENSITY = 15;
      const SPEED = 1;

      const ubicacionFinal = product.ubicacion && product.ubicacion !== 'Sin ubicación' 
        ? product.ubicacion 
        : (selectedLocation || 'Sin ubicación');

      port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600 });

      const qrUrl = `${api.defaults.baseURL}/qr-odoo/generate/${product.id}?ubicacion=${encodeURIComponent(ubicacionFinal)}`;
      const imgResponse = await fetch(qrUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const blob = await imgResponse.blob();
      const bitmap = await createImageBitmap(blob);

      // Calculamos la proporción del alto basándonos en el nuevo lienzo ancho de Python
      const proporcion = bitmap.height / bitmap.width; 
      const altoImagenProporcional = Math.round(QR_SIZE_PX * proporcion);

      const canvas = document.createElement('canvas');
      canvas.width = QR_SIZE_PX;
      // MODIFICADO: Incrementamos de +90 a +140 para dar espacio limpio a la Fecha y la Hora
      canvas.height = altoImagenProporcional + 140; 
      
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Dibujamos la imagen completa. El QR se verá chico y las letras ocuparán todo el ancho de 280
      ctx.drawImage(bitmap, 0, 0, QR_SIZE_PX, altoImagenProporcional);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      const bytesPerRow = Math.ceil(QR_SIZE_PX / 8);
      const bitmapBuffer = new Uint8Array(bytesPerRow * canvas.height);

      for (let y = 0; y < canvas.height; y++) {
        for (let xb = 0; xb < bytesPerRow; xb++) {
          let byte = 0;
          for (let bit = 0; bit < 8; bit++) {
            const x = xb * 8 + bit;
            if (x >= QR_SIZE_PX) break;
            const idx = (y * canvas.width + x) * 4; 
            const r = pixels[idx], g = pixels[idx + 1], b = pixels[idx + 2];
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;

            if (lum >= 200) {
              byte |= (0x80 >> bit);
            }
          }
          bitmapBuffer[y * bytesPerRow + xb] = byte;
        }
      }

      // === OBTENER FECHA Y HORA ACTUAL DEL SISTEMA ===
      const ahora = new Date();
      // Formato de Fecha: DD/MM/YYYY
      const fechaStr = ahora.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      // Formato de Hora: HH:MM AM/PM
      const horaStr = ahora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true });

      const encoder = new TextEncoder();

      const header = encoder.encode(
        `SIZE 48 mm,48 mm\r\nGAP 2 mm,0\r\nDIRECTION 0\r\nDENSITY ${DENSITY}\r\nSPEED ${SPEED}\r\nCLS\r\nBITMAP 24,5,${bytesPerRow},${canvas.height},0,`
      );

      const cantDespachada = printQuantities[product.id] || 1;

      // MODIFICADO: Agregamos los comandos TEXT consecutivos para Fecha y Hora
      // Cada línea baja de 24 a 26 píxeles verticalmente para una separación óptima
      const footer = encoder.encode(
        `\r\n` +
        `TEXT 24,${altoImagenProporcional + 15},"2",0,1,1,"DESPACHADO: ${cantDespachada}"\r\n` + 
        `TEXT 20,${altoImagenProporcional + 40},"2",0,1,1,"FECHA: ${fechaStr}"\r\n` + 
        `TEXT 15,${altoImagenProporcional + 60},"2",0,1,1,"HORA: ${horaStr}"\r\n` +   // se come la hora no la ajuste meee
        `\r\n` + 
        `PRINT 1,1\r\n`
      );

      const writer = port.writable.getWriter();
      await writer.write(header);
      await writer.write(bitmapBuffer);
      await writer.write(footer);

      writer.releaseLock();
      await port.close();

      setSnackbarMessage(`✓ Impreso en P1: ${product.name}`);
      setSnackbarSeverity('success');
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Error al imprimir en P1: " + err.message);
      setSnackbarSeverity('error');
    } finally {
      setPrintingId(null);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <Box sx={{ p: 3 }}>
      <Container maxWidth={false}>
        {/* Encabezado: Título y Buscador */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            Generador QR - Productos Odoo
          </Typography>

          <TextField
            variant="outlined"
            placeholder="Buscar por nombre, código de barras o referencia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 420 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
        </Box>

        {/* Selector de Ubicaciones Dinámicas */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
            <InputLabel>Ubicación de Origen</InputLabel>
            <Select
              value={selectedLocation}
              label="Ubicación de Origen"
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map((loc, index) => (
                <MenuItem key={index} value={loc.complete_name || loc.name}>
                  {loc.complete_name || loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Se usa si el producto no cuenta con una ubicación específica en Odoo
          </Typography>
        </Box>

        {/* Vista Principal / Carga */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nombre del Producto</strong></TableCell>
                  <TableCell align="center"><strong>Código de Barras</strong></TableCell>
                  <TableCell align="center"><strong>Referencia</strong></TableCell>
                  <TableCell><strong>Notas Internas</strong></TableCell>
                  <TableCell align="center"><strong>Ubicación Odoo</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>{product.name}</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{product.barcode || '—'}</TableCell>
                    <TableCell align="center">{product.default_code || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {product.description || 'Sin notas'}
                    </TableCell>
                    <TableCell align="center">
                      <strong>{product.ubicacion || '—'}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        
                        {/* Input numérico para colocar la cantidad despachada */}
                        <TextField
                          type="number"
                          size="small"
                          label="Cant."
                          inputProps={{ min: 1, style: { textAlign: 'center' } }}
                          value={printQuantities[product.id] || 1}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setPrintQuantities({
                              ...printQuantities,
                              [product.id]: isNaN(val) || val < 1 ? 1 : val
                            });
                          }}
                          disabled={printingId !== null}
                          sx={{ width: 75 }}
                        />

                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadQR(product)}
                          size="small"
                        >
                          Descargar
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={printingId === product.id ? <CircularProgress size={16} /> : <PrintIcon />}
                          onClick={() => handlePrintP1(product)}
                          disabled={printingId !== null}
                          size="small"
                        >
                          Imprimir P1
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredProducts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No se encontraron productos.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Mensajes Emergentes */}
        <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose}>
          <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default QrOdoo;